import React, { useEffect, useState } from 'react'
import { Link, Redirect } from 'react-router-dom'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { faArrowCircleLeft } from '@fortawesome/pro-regular-svg-icons/faArrowCircleLeft'

import { toast } from 'react-toastify'
import { Helmet } from 'react-helmet'

import { Heading, NotFoundMessage, UnauthorisedMessage } from '@peracto/peracto-ui'
import { GET_ONE, UPDATE, useClient, getSchemaFromResource } from '@peracto/client'
import { useConfig } from '@peracto/peracto-config'

const ElementAreasEditContainer = ({ children }) => {
    return (
        <div className="form-container">
            <Heading name="Edit Element Areas">
                <div className="!grow !flex !items-center !justify-end">
                    <Link
                        className="btn btn-outline-primary"
                        to="/element-areas"
                        data-testid="back-to-element-areas"
                    >
                        <FontAwesomeIcon icon={faArrowCircleLeft} className="!mr-2" />
                        Back to Element Areas
                    </Link>
                </div>
            </Heading>
            {children}
        </div>
    )
}

const formatDataForForm = data => {
    const elementArea = {
        id: null,
        name: null,
    }

    if (data?.contentElementAreas?.length > 0) {
        if (data.contentElementAreas[0].content?.id) {
            elementArea.id = `/contents/${data.contentElementAreas[0].content.id}`
            elementArea.name = data.contentElementAreas[0].content?.title || '-'
        }
    }

    return { elementArea }
}

const ElementAreasEdit = ({
    ElementAreasForm,
    match: {
        params: { id },
    },
}) => {
    const { client, getResource } = useClient()
    const [loading, setLoading] = useState(true)
    const [unauthorised, setUnauthorised] = useState(false)
    const [notFound, setNotFound] = useState(false)
    const [redirect, setRedirect] = useState()
    const config = useConfig()
    const { content } = config.get('features', {})
    const allowDisableContentElementAreas = content?.allowDisableContentElementAreas || false

    const [elementAreas, setElementAreas] = useState()
    const [elementAreaID, setElementAreaID] = useState()

    const fetchElementAreas = async () => {
        try {
            const { data, response } = await client(GET_ONE, 'content-elements', {
                id: `/content-elements/${id}`,
            })

            const formattedData = {
                ...formatDataForForm(data),
                ...(allowDisableContentElementAreas && {
                    enabled: data?.contentElementAreas[0]?.enabled,
                }),
            }

            if (data?.contentElementAreas?.length > 0 && data?.contentElementAreas[0].id) {
                setElementAreaID(data.contentElementAreas[0].id)
            }

            setElementAreas(formattedData)
            setLoading(false)

            if (response.status === 404) {
                setRedirect('/element-areas')
            }
        } catch (e) {
            console.error(e)

            if (e.status === 403) {
                setUnauthorised(true)
            }

            if (e.status === 404) {
                setNotFound(true)
            }

            setLoading(false)
            setRedirect('/element-areas')
        }
    }

    useEffect(() => {
        fetchElementAreas()
    }, [])

    const schema = getSchemaFromResource(getResource('content-elements'))

    const onSubmit = async (data, actions) => {
        const requestData = {
            contentElementAreas: [
                {
                    elementCode: id,
                    content: data?.elementArea?.id,
                    id: elementAreaID,
                    ...(allowDisableContentElementAreas && { enabled: data?.enabled }),
                },
            ],
        }

        try {
            const response = await client(UPDATE, 'content-elements', {
                id: `/content-elements/${id}`,
                data: requestData,
            })

            if (response.data.violations && response.data.violations.length > 0) {
                // Display errors for invalid fields
                actions.setSubmitting(false)
                response.data.violations.map(error => {
                    actions.setFieldError(error.propertyPath, error.message)
                })
            } else {
                actions.setSubmitting(false)
                toast.success('Element Area successfully updated!')
                setRedirect('/element-areas')
            }
        } catch (e) {
            console.error(e)
            toast.error(
                e?.error?.body?.hasOwnProperty('hydra:description')
                    ? e.error.body['hydra:description']
                    : 'Whoops, there was a problem...'
            )
            if (e?.error?.body?.violations?.length > 0) {
                // Display errors for invalid fields
                e.error.body.violations.map(error => {
                    actions.setFieldError(error.propertyPath, error.message)
                })
            }
            actions.setSubmitting(false)
        }
    }

    if (loading) {
        return (
            <ElementAreasEditContainer>
                <div className="card">
                    <div className="card-body">Loading...</div>
                </div>
            </ElementAreasEditContainer>
        )
    }

    if (unauthorised) {
        return <UnauthorisedMessage />
    }

    if (notFound) {
        return (
            <NotFoundMessage
                url="/element-areas"
                message="The element area you're looking for could not be found"
                buttonLabel="Go to Element Areas"
            />
        )
    }

    return (
        <ElementAreasEditContainer>
            {redirect && <Redirect to={redirect} />}

            <Helmet>
                <title>{'Element Areas'} | Edit | Peracto</title>
            </Helmet>

            <ElementAreasForm values={elementAreas} onSubmit={onSubmit} schema={schema} />
        </ElementAreasEditContainer>
    )
}

export default ElementAreasEdit
